// ─── System Map Transformer ──────────────────────────────────────────────────
//
// Builds the SystemOverview view model from computed pillar results +
// optional deltas + optional sparkline data. Each pillar is wrapped as a
// SystemOverviewItem with a trend direction derived from the delta.
//
// Pure function. No React, no side effects. Called by buildDashboardVM.
// ──────────────────────────────────────────────────────────────────────────────

import type { PillarResult } from '../pillarScoring';
import type {
  SystemOverviewItem,
  SystemOverview,
  TrendDirection,
  TrustIndicators,
  SystemStatusSummary,
  SeverityBand,
} from './types';

// ─── Inputs ──────────────────────────────────────────────────────────────────

export interface SystemMapInput {
  pillarResults: Record<string, PillarResult>;
  pillarDeltas?: Record<string, number>;
  sparklineData?: Record<string, number[]>;
  /** Pre-computed trust chip to embed in the section. */
  trust: TrustIndicators;
  /** Display order for pillars. */
  order?: string[];
}

/** Default pillar display order (matches current dashboard convention). */
const DEFAULT_ORDER = ['sleep', 'movement', 'nutrition', 'stress', 'social', 'substance'];

/** Human-readable names — falls back to the raw key. */
const PILLAR_NAMES: Record<string, string> = {
  sleep: 'Sleep',
  movement: 'Movement',
  nutrition: 'Nutrition',
  stress: 'Stress',
  social: 'Social',
  substance: 'Substances',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Classify a pillar delta as a trend direction. Thresholds match the visual
 * trend arrows used in PillarCard: ≤±2 is "stable", >3 is improving, <-3 is
 * declining. Anything in between is "stable" (no visual noise).
 */
export function classifyDelta(delta: number | null | undefined): TrendDirection {
  if (delta == null) return 'unknown';
  if (delta > 3) return 'improving';
  if (delta < -3) return 'declining';
  return 'stable';
}

/**
 * Aggregate the system-wide status across all tracked pillars. Buckets each
 * pillar by its current score band and returns a structured summary that
 * drives the System Overview header chip.
 *
 * Thresholds match PillarCard severity bands:
 *   score >= 60 -> nominal (good + excellent)
 *   40 <= score < 60 -> fair
 *   score < 40 -> needs attention
 *   score == null -> incomplete data
 */
export function computeSystemStatusSummary(
  items: SystemOverviewItem[],
): SystemStatusSummary {
  let nominal = 0;
  let fair = 0;
  let needsAttention = 0;
  let incomplete = 0;

  for (const item of items) {
    const score = item.pillar.score;
    if (score == null) {
      incomplete++;
    } else if (score >= 60) {
      nominal++;
    } else if (score >= 40) {
      fair++;
    } else {
      needsAttention++;
    }
  }

  const total = items.length;

  // Build a single-line summary prioritizing the most important categories.
  // Always starts with the nominal count so it reads positively when possible.
  const parts: string[] = [];
  parts.push(`${nominal} of ${total} systems nominal`);
  if (needsAttention > 0) {
    parts.push(`${needsAttention} needs attention`);
  }
  if (fair > 0 && needsAttention === 0) {
    // Only surface "fair" as a separate bucket when nothing is more urgent
    parts.push(`${fair} running strained`);
  }
  if (incomplete > 0) {
    parts.push(`${incomplete} incomplete data`);
  }

  // Overall band for the chip color: worst-case wins
  let overallBand: SeverityBand = 'no_data';
  if (needsAttention > 0) {
    overallBand = 'attention';
  } else if (fair > 0) {
    overallBand = 'fair';
  } else if (nominal > 0) {
    // Distinguish excellent (>=80 majority) from good — approximate by
    // looking at the scored items' mean.
    const scored = items.filter((i) => i.pillar.score != null);
    if (scored.length > 0) {
      const mean = scored.reduce((s, i) => s + (i.pillar.score ?? 0), 0) / scored.length;
      overallBand = mean >= 80 ? 'excellent' : 'good';
    } else {
      overallBand = 'good';
    }
  }

  return {
    total,
    nominal,
    fair,
    needsAttention,
    incomplete,
    text: parts.join(' · '),
    overallBand,
  };
}

// ─── Main transformer ────────────────────────────────────────────────────────

export function computeSystemMap(input: SystemMapInput): SystemOverview {
  const {
    pillarResults,
    pillarDeltas = {},
    sparklineData = {},
    trust,
    order = DEFAULT_ORDER,
  } = input;

  const items: SystemOverviewItem[] = order
    .filter((key) => pillarResults[key] !== undefined)
    .map((key) => {
      const pillar = pillarResults[key];
      const delta = pillarDeltas[key];
      return {
        key,
        name: PILLAR_NAMES[key] ?? key,
        pillar,
        sparkline: sparklineData[key] ?? [],
        delta: delta == null || !isFinite(delta) ? null : delta,
        trend: classifyDelta(delta),
      };
    });

  const statusSummary = computeSystemStatusSummary(items);

  return { items, trust, statusSummary };
}
